export type FieldType = 'color' | 'px' | 'percentage' | 'em' | 'number' | 'font' | 'select' | 'image';

export type VariableGroup =
  | 'paper'
  | 'paper-background'
  | 'typography-h1'
  | 'typography-h2'
  | 'typography-h3'
  | 'typography-h4'
  | 'typography-h5'
  | 'typography-h6'
  | 'typography-body'
  | 'typography-td'
  | 'typography-th'
  | 'typography-li'
  | 'typography-scaling'
  | 'layout-padding'
  | 'layout-margin'
  | 'layout-spacing'
  | 'block-spacing'
  | 'header'
  | 'footer'
  | 'header-footer-text'
  | 'header-typography'
  | 'footer-typography'
  | 'table-of-contents'
  | 'derived-primary'
  | 'derived-secondary'
  | 'derived-tertiary'
  | 'derived-quaternary'
  | 'derived-white'
  | 'derived-black';

export interface VariableDefinition {
  key: string;
  label: string;
  type: FieldType;
  group: VariableGroup;
  editable: boolean;
  defaultValue: string | number;
  options?: { value: string; label: string }[];
  emptyValue?: string | null;
  placeholder?: string;
}

export const variableSchema: VariableDefinition[] = [
  // Paper
  {
    key: 'pageSize',
    label: 'dc.var.paper-size',
    type: 'select',
    group: 'paper',
    editable: true,
    defaultValue: 'A4 portrait',
    options: [
      { value: 'A4 portrait', label: 'dc.opt.a4-portrait' },
      { value: 'A4 landscape', label: 'dc.opt.a4-landscape' },
      { value: 'A3 portrait', label: 'dc.opt.a3-portrait' },
      { value: 'A3 landscape', label: 'dc.opt.a3-landscape' },
      { value: 'A5 portrait', label: 'dc.opt.a5-portrait' },
      { value: 'A5 landscape', label: 'dc.opt.a5-landscape' },
      { value: 'letter portrait', label: 'dc.opt.letter-portrait' },
      { value: 'letter landscape', label: 'dc.opt.letter-landscape' },
      { value: 'legal portrait', label: 'dc.opt.legal-portrait' },
      { value: 'legal landscape', label: 'dc.opt.legal-landscape' },
    ],
  },
  { key: 'pageWidth', label: 'dc.var.width', type: 'px', group: 'paper', editable: true, defaultValue: 1123 },
  { key: 'pageHeight', label: 'dc.var.height', type: 'px', group: 'paper', editable: true, defaultValue: 794 },
  { key: 'xrpBackgroundImage', label: 'dc.var.background-image', type: 'image', group: 'paper-background', editable: true, defaultValue: 'none' },

  // Typography — h1
  { key: 'h1-size', label: 'dc.var.size', type: 'px', group: 'typography-h1', editable: true, defaultValue: 34 },
  { key: 'h1-line', label: 'dc.var.line-height', type: 'px', group: 'typography-h1', editable: true, defaultValue: 38 },
  { key: 'h1-track', label: 'dc.var.letter-spacing', type: 'em', group: 'typography-h1', editable: true, defaultValue: 0.02 },
  {
    key: 'h1-weight',
    label: 'dc.var.font-weight',
    type: 'number',
    group: 'typography-h1',
    editable: true,
    defaultValue: '',
    emptyValue: 'normal',
    placeholder: 'normal',
  },
  {
    key: 'h1-weight-bold',
    label: 'dc.var.font-weight-bold',
    type: 'number',
    group: 'typography-h1',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: '700',
  },
  {
    key: 'h1-weight-italic',
    label: 'dc.var.font-weight-italic',
    type: 'number',
    group: 'typography-h1',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: 'normal',
  },
  { key: 'h1-font-regular', label: 'graphical-variable.normal', type: 'font', group: 'typography-h1', editable: true, defaultValue: '' },
  { key: 'h1-font-bold', label: 'graphical-variable.bold', type: 'font', group: 'typography-h1', editable: true, defaultValue: '' },
  { key: 'h1-font-italic', label: 'graphical-variable.italic', type: 'font', group: 'typography-h1', editable: true, defaultValue: '' },
  { key: 'h1-color', label: 'dc.var.color', type: 'color', group: 'typography-h1', editable: true, defaultValue: '' },

  // Typography — h2
  { key: 'h2-size', label: 'dc.var.size', type: 'px', group: 'typography-h2', editable: true, defaultValue: 20 },
  { key: 'h2-line', label: 'dc.var.line-height', type: 'px', group: 'typography-h2', editable: true, defaultValue: 26 },
  { key: 'h2-track', label: 'dc.var.letter-spacing', type: 'em', group: 'typography-h2', editable: true, defaultValue: 0.015 },
  {
    key: 'h2-weight',
    label: 'dc.var.font-weight',
    type: 'number',
    group: 'typography-h2',
    editable: true,
    defaultValue: '',
    emptyValue: 'normal',
    placeholder: 'normal',
  },
  {
    key: 'h2-weight-bold',
    label: 'dc.var.font-weight-bold',
    type: 'number',
    group: 'typography-h2',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: '700',
  },
  {
    key: 'h2-weight-italic',
    label: 'dc.var.font-weight-italic',
    type: 'number',
    group: 'typography-h2',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: 'normal',
  },
  { key: 'h2-font-regular', label: 'graphical-variable.normal', type: 'font', group: 'typography-h2', editable: true, defaultValue: '' },
  { key: 'h2-font-bold', label: 'graphical-variable.bold', type: 'font', group: 'typography-h2', editable: true, defaultValue: '' },
  { key: 'h2-font-italic', label: 'graphical-variable.italic', type: 'font', group: 'typography-h2', editable: true, defaultValue: '' },
  { key: 'h2-color', label: 'dc.var.color', type: 'color', group: 'typography-h2', editable: true, defaultValue: '' },

  // Typography — h3
  { key: 'h3-size', label: 'dc.var.size', type: 'px', group: 'typography-h3', editable: true, defaultValue: 16 },
  { key: 'h3-line', label: 'dc.var.line-height', type: 'px', group: 'typography-h3', editable: true, defaultValue: 22 },
  { key: 'h3-track', label: 'dc.var.letter-spacing', type: 'em', group: 'typography-h3', editable: true, defaultValue: 0.015 },
  {
    key: 'h3-weight',
    label: 'dc.var.font-weight',
    type: 'number',
    group: 'typography-h3',
    editable: true,
    defaultValue: '',
    emptyValue: 'normal',
    placeholder: 'normal',
  },
  {
    key: 'h3-weight-bold',
    label: 'dc.var.font-weight-bold',
    type: 'number',
    group: 'typography-h3',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: '700',
  },
  {
    key: 'h3-weight-italic',
    label: 'dc.var.font-weight-italic',
    type: 'number',
    group: 'typography-h3',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: 'normal',
  },
  { key: 'h3-font-regular', label: 'graphical-variable.normal', type: 'font', group: 'typography-h3', editable: true, defaultValue: '' },
  { key: 'h3-font-bold', label: 'graphical-variable.bold', type: 'font', group: 'typography-h3', editable: true, defaultValue: '' },
  { key: 'h3-font-italic', label: 'graphical-variable.italic', type: 'font', group: 'typography-h3', editable: true, defaultValue: '' },
  { key: 'h3-color', label: 'dc.var.color', type: 'color', group: 'typography-h3', editable: true, defaultValue: '' },

  // Typography — h4
  { key: 'h4-size', label: 'dc.var.size', type: 'px', group: 'typography-h4', editable: true, defaultValue: 14 },
  { key: 'h4-line', label: 'dc.var.line-height', type: 'px', group: 'typography-h4', editable: true, defaultValue: 20 },
  { key: 'h4-track', label: 'dc.var.letter-spacing', type: 'em', group: 'typography-h4', editable: true, defaultValue: 0.01 },
  {
    key: 'h4-weight',
    label: 'dc.var.font-weight',
    type: 'number',
    group: 'typography-h4',
    editable: true,
    defaultValue: '',
    emptyValue: 'normal',
    placeholder: 'normal',
  },
  {
    key: 'h4-weight-bold',
    label: 'dc.var.font-weight-bold',
    type: 'number',
    group: 'typography-h4',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: '700',
  },
  {
    key: 'h4-weight-italic',
    label: 'dc.var.font-weight-italic',
    type: 'number',
    group: 'typography-h4',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: 'normal',
  },
  { key: 'h4-font-regular', label: 'graphical-variable.normal', type: 'font', group: 'typography-h4', editable: true, defaultValue: '' },
  { key: 'h4-font-bold', label: 'graphical-variable.bold', type: 'font', group: 'typography-h4', editable: true, defaultValue: '' },
  { key: 'h4-font-italic', label: 'graphical-variable.italic', type: 'font', group: 'typography-h4', editable: true, defaultValue: '' },
  { key: 'h4-color', label: 'dc.var.color', type: 'color', group: 'typography-h4', editable: true, defaultValue: '' },

  // Typography — h5
  { key: 'h5-size', label: 'dc.var.size', type: 'px', group: 'typography-h5', editable: true, defaultValue: 12 },
  { key: 'h5-line', label: 'dc.var.line-height', type: 'px', group: 'typography-h5', editable: true, defaultValue: 18 },
  { key: 'h5-track', label: 'dc.var.letter-spacing', type: 'em', group: 'typography-h5', editable: true, defaultValue: 0.01 },
  {
    key: 'h5-weight',
    label: 'dc.var.font-weight',
    type: 'number',
    group: 'typography-h5',
    editable: true,
    defaultValue: '',
    emptyValue: 'normal',
    placeholder: 'normal',
  },
  {
    key: 'h5-weight-bold',
    label: 'dc.var.font-weight-bold',
    type: 'number',
    group: 'typography-h5',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: '700',
  },
  {
    key: 'h5-weight-italic',
    label: 'dc.var.font-weight-italic',
    type: 'number',
    group: 'typography-h5',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: 'normal',
  },
  { key: 'h5-font-regular', label: 'graphical-variable.normal', type: 'font', group: 'typography-h5', editable: true, defaultValue: '' },
  { key: 'h5-font-bold', label: 'graphical-variable.bold', type: 'font', group: 'typography-h5', editable: true, defaultValue: '' },
  { key: 'h5-font-italic', label: 'graphical-variable.italic', type: 'font', group: 'typography-h5', editable: true, defaultValue: '' },
  { key: 'h5-color', label: 'dc.var.color', type: 'color', group: 'typography-h5', editable: true, defaultValue: '' },

  // Typography — h6
  { key: 'h6-size', label: 'dc.var.size', type: 'px', group: 'typography-h6', editable: true, defaultValue: 11 },
  { key: 'h6-line', label: 'dc.var.line-height', type: 'px', group: 'typography-h6', editable: true, defaultValue: 16 },
  { key: 'h6-track', label: 'dc.var.letter-spacing', type: 'em', group: 'typography-h6', editable: true, defaultValue: 0.01 },
  {
    key: 'h6-weight',
    label: 'dc.var.font-weight',
    type: 'number',
    group: 'typography-h6',
    editable: true,
    defaultValue: '',
    emptyValue: 'normal',
    placeholder: 'normal',
  },
  {
    key: 'h6-weight-bold',
    label: 'dc.var.font-weight-bold',
    type: 'number',
    group: 'typography-h6',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: '700',
  },
  {
    key: 'h6-weight-italic',
    label: 'dc.var.font-weight-italic',
    type: 'number',
    group: 'typography-h6',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: 'normal',
  },
  { key: 'h6-font-regular', label: 'graphical-variable.normal', type: 'font', group: 'typography-h6', editable: true, defaultValue: '' },
  { key: 'h6-font-bold', label: 'graphical-variable.bold', type: 'font', group: 'typography-h6', editable: true, defaultValue: '' },
  { key: 'h6-font-italic', label: 'graphical-variable.italic', type: 'font', group: 'typography-h6', editable: true, defaultValue: '' },
  { key: 'h6-color', label: 'dc.var.color', type: 'color', group: 'typography-h6', editable: true, defaultValue: '' },

  // Typography — body
  { key: 'p-size', label: 'dc.var.size', type: 'px', group: 'typography-body', editable: true, defaultValue: 12 },
  { key: 'p-line', label: 'dc.var.line-height', type: 'px', group: 'typography-body', editable: true, defaultValue: 18 },
  { key: 'p-track', label: 'dc.var.letter-spacing', type: 'em', group: 'typography-body', editable: true, defaultValue: 0 },
  {
    key: 'p-weight',
    label: 'dc.var.font-weight',
    type: 'number',
    group: 'typography-body',
    editable: true,
    defaultValue: '',
    emptyValue: 'normal',
    placeholder: 'normal',
  },
  {
    key: 'p-weight-bold',
    label: 'dc.var.font-weight-bold',
    type: 'number',
    group: 'typography-body',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: '700',
  },
  {
    key: 'p-weight-italic',
    label: 'dc.var.font-weight-italic',
    type: 'number',
    group: 'typography-body',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: 'normal',
  },

  // Typography — td
  { key: 'td-size', label: 'dc.var.size', type: 'px', group: 'typography-td', editable: true, defaultValue: 12 },
  { key: 'td-line', label: 'dc.var.line-height', type: 'px', group: 'typography-td', editable: true, defaultValue: 18 },
  { key: 'td-track', label: 'dc.var.letter-spacing', type: 'em', group: 'typography-td', editable: true, defaultValue: 0 },
  {
    key: 'td-weight',
    label: 'dc.var.font-weight',
    type: 'number',
    group: 'typography-td',
    editable: true,
    defaultValue: '',
    emptyValue: 'normal',
    placeholder: 'normal',
  },
  {
    key: 'td-weight-bold',
    label: 'dc.var.font-weight-bold',
    type: 'number',
    group: 'typography-td',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: '700',
  },
  {
    key: 'td-weight-italic',
    label: 'dc.var.font-weight-italic',
    type: 'number',
    group: 'typography-td',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: 'normal',
  },

  // Typography — th
  { key: 'th-size', label: 'dc.var.size', type: 'px', group: 'typography-th', editable: true, defaultValue: 12 },
  { key: 'th-line', label: 'dc.var.line-height', type: 'px', group: 'typography-th', editable: true, defaultValue: 18 },
  { key: 'th-track', label: 'dc.var.letter-spacing', type: 'em', group: 'typography-th', editable: true, defaultValue: 0.02 },
  {
    key: 'th-weight',
    label: 'dc.var.font-weight',
    type: 'number',
    group: 'typography-th',
    editable: true,
    defaultValue: '',
    emptyValue: 'normal',
    placeholder: 'normal',
  },
  {
    key: 'th-weight-bold',
    label: 'dc.var.font-weight-bold',
    type: 'number',
    group: 'typography-th',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: '700',
  },
  {
    key: 'th-weight-italic',
    label: 'dc.var.font-weight-italic',
    type: 'number',
    group: 'typography-th',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: 'normal',
  },

  // Typography — li
  { key: 'li-size', label: 'dc.var.size', type: 'px', group: 'typography-li', editable: true, defaultValue: 12 },
  { key: 'li-line', label: 'dc.var.line-height', type: 'px', group: 'typography-li', editable: true, defaultValue: 18 },
  { key: 'li-track', label: 'dc.var.letter-spacing', type: 'em', group: 'typography-li', editable: true, defaultValue: 0 },
  {
    key: 'li-weight',
    label: 'dc.var.font-weight',
    type: 'number',
    group: 'typography-li',
    editable: true,
    defaultValue: '',
    emptyValue: 'normal',
    placeholder: 'normal',
  },
  {
    key: 'li-weight-bold',
    label: 'dc.var.font-weight-bold',
    type: 'number',
    group: 'typography-li',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: '700',
  },
  {
    key: 'li-weight-italic',
    label: 'dc.var.font-weight-italic',
    type: 'number',
    group: 'typography-li',
    editable: true,
    defaultValue: '',
    emptyValue: null,
    placeholder: 'normal',
  },

  // Typography — scaling
  { key: 'xrpFontSizeBase', label: 'dc.var.type-scale', type: 'number', group: 'typography-scaling', editable: true, defaultValue: 1 },
  { key: 'xrpLineHeightBase', label: 'dc.var.line-height-scale', type: 'number', group: 'typography-scaling', editable: true, defaultValue: 1 },
  { key: 'xrpLetterSpacingBase', label: 'dc.var.tracking-scale', type: 'number', group: 'typography-scaling', editable: true, defaultValue: 1 },

  // Layout — padding
  { key: 'wrapperPaddingTop', label: 'dc.var.top', type: 'px', group: 'layout-padding', editable: true, defaultValue: 64 },
  { key: 'wrapperPaddingRight', label: 'dc.var.right', type: 'px', group: 'layout-padding', editable: true, defaultValue: 53 },
  { key: 'wrapperPaddingBottom', label: 'dc.var.bottom', type: 'px', group: 'layout-padding', editable: true, defaultValue: 54 },
  { key: 'wrapperPaddingLeft', label: 'dc.var.left', type: 'px', group: 'layout-padding', editable: true, defaultValue: 53 },

  // Layout — margin
  { key: 'wrapperMarginTop', label: 'dc.var.top', type: 'px', group: 'layout-margin', editable: true, defaultValue: 22 },
  { key: 'wrapperMarginRight', label: 'dc.var.right', type: 'px', group: 'layout-margin', editable: true, defaultValue: 22 },
  { key: 'wrapperMarginBottom', label: 'dc.var.bottom', type: 'px', group: 'layout-margin', editable: true, defaultValue: 22 },
  { key: 'wrapperMarginLeft', label: 'dc.var.left', type: 'px', group: 'layout-margin', editable: true, defaultValue: 22 },

  // Layout — row/column spacing
  { key: 'rowMarginTop', label: 'dc.var.row-spacing', type: 'px', group: 'layout-spacing', editable: true, defaultValue: 16 },
  { key: 'colGap', label: 'dc.var.col-gap', type: 'px', group: 'layout-spacing', editable: true, defaultValue: 11 },

  // Layout — heading/paragraph/list spacing
  { key: 'space-2', label: 'dc.var.heading-spacing', type: 'px', group: 'block-spacing', editable: true, defaultValue: 8 },
  { key: 'space-3', label: 'dc.var.paragraph-spacing', type: 'px', group: 'block-spacing', editable: true, defaultValue: 12 },
  { key: 'space-4', label: 'dc.var.list-indent', type: 'px', group: 'block-spacing', editable: true, defaultValue: 16 },

  // Header
  { key: 'headerTop', label: 'dc.var.offset-top', type: 'px', group: 'header', editable: true, defaultValue: 9 },
  { key: 'headerReserve', label: 'dc.var.reserved-space', type: 'px', group: 'header', editable: true, defaultValue: 0 },

  // Footer
  { key: 'footerBottom', label: 'dc.var.offset-bottom', type: 'px', group: 'footer', editable: true, defaultValue: 12 },
  { key: 'footerReserve', label: 'dc.var.reserved-space', type: 'px', group: 'footer', editable: true, defaultValue: 36 },

  // Header & footer shared typography (fallback when per-area vars below are null)
  { key: 'headerFooterFontSize', label: 'dc.var.font-size', type: 'px', group: 'header-footer-text', editable: true, defaultValue: 9 },
  { key: 'headerFooterLineHeight', label: 'dc.var.line-height', type: 'px', group: 'header-footer-text', editable: true, defaultValue: 12 },
  { key: 'headerFooterLetterSpacing', label: 'dc.var.letter-spacing', type: 'em', group: 'header-footer-text', editable: true, defaultValue: 0.08 },

  // Header-specific typography (same model as header-typography above)
  { key: 'headerFontSize', label: 'dc.var.font-size', type: 'px', group: 'header-typography', editable: true, defaultValue: 9 },
  { key: 'headerLineHeight', label: 'dc.var.line-height', type: 'px', group: 'header-typography', editable: true, defaultValue: 12 },
  { key: 'headerLetterSpacing', label: 'dc.var.letter-spacing', type: 'em', group: 'header-typography', editable: true, defaultValue: 0.08 },

  // Footer-specific typography (same model as header-typography above)
  { key: 'footerFontSize', label: 'dc.var.font-size', type: 'px', group: 'footer-typography', editable: true, defaultValue: 9 },
  { key: 'footerLineHeight', label: 'dc.var.line-height', type: 'px', group: 'footer-typography', editable: true, defaultValue: 12 },
  { key: 'footerLetterSpacing', label: 'dc.var.letter-spacing', type: 'em', group: 'footer-typography', editable: true, defaultValue: 0.08 },

  { key: 'tocFontSize', label: 'dc.var.font-size', type: 'px', group: 'table-of-contents', editable: true, defaultValue: 9 },
  { key: 'tocLineHeight', label: 'dc.var.line-height', type: 'px', group: 'table-of-contents', editable: true, defaultValue: 17 },
  { key: 'tocLetterSpacing', label: 'dc.var.letter-spacing', type: 'em', group: 'table-of-contents', editable: true, defaultValue: 0 },
  { key: 'tocColor', label: 'dc.var.color', type: 'color', group: 'table-of-contents', editable: true, defaultValue: '' },
  { key: 'tocIndent', label: 'dc.var.toc-indent', type: 'px', group: 'table-of-contents', editable: true, defaultValue: 12 },

  // Derived primary (read-only)
  { key: 'primaryColor100', label: '100%', type: 'color', group: 'derived-primary', editable: false, defaultValue: '' },
  { key: 'primaryColor75', label: '75%', type: 'color', group: 'derived-primary', editable: false, defaultValue: '' },
  { key: 'primaryColor50', label: '50%', type: 'color', group: 'derived-primary', editable: false, defaultValue: '' },
  { key: 'primaryColor25', label: '25%', type: 'color', group: 'derived-primary', editable: false, defaultValue: '' },
  { key: 'primaryColor10', label: '10%', type: 'color', group: 'derived-primary', editable: false, defaultValue: '' },

  // Derived secondary (read-only)
  { key: 'secondaryColor100', label: '100%', type: 'color', group: 'derived-secondary', editable: false, defaultValue: '' },
  { key: 'secondaryColor75', label: '75%', type: 'color', group: 'derived-secondary', editable: false, defaultValue: '' },
  { key: 'secondaryColor50', label: '50%', type: 'color', group: 'derived-secondary', editable: false, defaultValue: '' },
  { key: 'secondaryColor25', label: '25%', type: 'color', group: 'derived-secondary', editable: false, defaultValue: '' },
  { key: 'secondaryColor10', label: '10%', type: 'color', group: 'derived-secondary', editable: false, defaultValue: '' },

  // Derived tertiary (read-only)
  { key: 'tertiaryColor100', label: '100%', type: 'color', group: 'derived-tertiary', editable: false, defaultValue: '' },
  { key: 'tertiaryColor75', label: '75%', type: 'color', group: 'derived-tertiary', editable: false, defaultValue: '' },
  { key: 'tertiaryColor50', label: '50%', type: 'color', group: 'derived-tertiary', editable: false, defaultValue: '' },
  { key: 'tertiaryColor25', label: '25%', type: 'color', group: 'derived-tertiary', editable: false, defaultValue: '' },
  { key: 'tertiaryColor10', label: '10%', type: 'color', group: 'derived-tertiary', editable: false, defaultValue: '' },

  // Derived quaternary (read-only)
  { key: 'quaternaryColor100', label: '100%', type: 'color', group: 'derived-quaternary', editable: false, defaultValue: '' },
  { key: 'quaternaryColor75', label: '75%', type: 'color', group: 'derived-quaternary', editable: false, defaultValue: '' },
  { key: 'quaternaryColor50', label: '50%', type: 'color', group: 'derived-quaternary', editable: false, defaultValue: '' },
  { key: 'quaternaryColor25', label: '25%', type: 'color', group: 'derived-quaternary', editable: false, defaultValue: '' },
  { key: 'quaternaryColor10', label: '10%', type: 'color', group: 'derived-quaternary', editable: false, defaultValue: '' },

  // Derived white (read-only)
  { key: 'white100', label: '100%', type: 'color', group: 'derived-white', editable: false, defaultValue: '' },
  { key: 'white75', label: '75%', type: 'color', group: 'derived-white', editable: false, defaultValue: '' },
  { key: 'white50', label: '50%', type: 'color', group: 'derived-white', editable: false, defaultValue: '' },
  { key: 'white25', label: '25%', type: 'color', group: 'derived-white', editable: false, defaultValue: '' },
  { key: 'white10', label: '10%', type: 'color', group: 'derived-white', editable: false, defaultValue: '' },

  // Derived black (read-only)
  { key: 'black100', label: '100%', type: 'color', group: 'derived-black', editable: false, defaultValue: '' },
  { key: 'black75', label: '75%', type: 'color', group: 'derived-black', editable: false, defaultValue: '' },
  { key: 'black50', label: '50%', type: 'color', group: 'derived-black', editable: false, defaultValue: '' },
  { key: 'black25', label: '25%', type: 'color', group: 'derived-black', editable: false, defaultValue: '' },
  { key: 'black10', label: '10%', type: 'color', group: 'derived-black', editable: false, defaultValue: '' },
];

export const groupLabels: Record<VariableGroup, string> = {
  paper: 'dc.group.paper',
  'paper-background': 'dc.group.paper-background',
  'typography-h1': 'dc.group.typography-h1',
  'typography-h2': 'dc.group.typography-h2',
  'typography-h3': 'dc.group.typography-h3',
  'typography-h4': 'dc.group.typography-h4',
  'typography-h5': 'dc.group.typography-h5',
  'typography-h6': 'dc.group.typography-h6',
  'typography-body': 'dc.group.typography-body',
  'typography-td': 'dc.group.typography-td',
  'typography-th': 'dc.group.typography-th',
  'typography-li': 'dc.group.typography-li',
  'typography-scaling': 'dc.group.typography-scaling',
  'layout-padding': 'dc.group.layout-padding',
  'layout-margin': 'dc.group.layout-margin',
  'layout-spacing': 'dc.group.layout-spacing',
  'block-spacing': 'dc.group.block-spacing',
  header: 'dc.group.header',
  footer: 'dc.group.footer',
  'header-footer-text': 'dc.group.header-footer-text',
  'header-typography': 'dc.group.header-typography',
  'footer-typography': 'dc.group.footer-typography',
  'table-of-contents': 'dc.group.table-of-contents',
  'derived-primary': 'dc.group.derived-primary',
  'derived-secondary': 'dc.group.derived-secondary',
  'derived-tertiary': 'dc.group.derived-tertiary',
  'derived-quaternary': 'dc.group.derived-quaternary',
  'derived-white': 'dc.group.derived-white',
  'derived-black': 'dc.group.derived-black',
};

export function getSchemaByGroup(group: VariableGroup): VariableDefinition[] {
  return variableSchema.filter((v) => v.group === group);
}

export function getEditableGroups(): VariableGroup[] {
  return Array.from(new Set(variableSchema.filter((v) => v.editable).map((v) => v.group)));
}

export const NUMERIC_FIELD_TYPES = new Set<FieldType>(['px', 'percentage', 'em', 'number']);

export function getUnitSuffix(type: FieldType): string {
  switch (type) {
    case 'px':
      return 'px';
    case 'percentage':
      return '%';
    case 'em':
      return 'em';
    default:
      return '';
  }
}
